var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { AbortableNavigateEvent, AbortableNavigateEventListener, AbortableSubmitEventListener, NavigateEvent, NavigateEventListener, RenderEvent, RenderEventListener, SubmitEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { createAbort } from '@philips-internal/oswald-ui-utils';
import { AdjacentStep, editStep, findAdjacentStepIdentifier, getIdentifiers, submitStep, updateStages } from './wizard-utils.js';
class WizardAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /* @event */
        this.onAfterNavigate = new NavigateEventListener();
        /* @event */
        this.onBeforeSubmit = new AbortableSubmitEventListener();
        /* @event */
        this.onBeforeCancel = new AbortableSubmitEventListener();
        /* @event */
        this.onCancel = new SubmitEventListener();
        /* @event */
        this.onDynamicContentRender = new RenderEventListener();
        /* @event */
        this.onNavigate = new AbortableNavigateEventListener();
        /* @event */
        this.onSubmit = new SubmitEventListener();
    }
    /**
     * Gets the current step.
     *
     * @returns the current wizard step.
     */
    getStep(identifier) {
        const { steps } = this.state;
        return steps[identifier];
    }
    /**
     * Gets the confirm button expression.
     *
     * @returns the confirm button expression.
     */
    getConfirmExpression() {
        return this.state.confirmExpression;
    }
    /**
     * Sets the confirm button expression.
     *
     * @param confirmExpression - The confirm button expression.
     */
    setConfirmExpression(confirmExpression) {
        this.setState({ confirmExpression });
    }
    /**
     * Gets the current step identifier.
     *
     * @returns The current step identifier.
     */
    getCurrentStepIdentifier() {
        return this.state.current;
    }
    /**
     * Gets the current group identifier.
     *
     * @returns The current group identifier.
     */
    getCurrentGroupIdentifier() {
        const { contextGroups, current } = this.state;
        if (!contextGroups.size) {
            return null;
        }
        const group = contextGroups.get(current);
        if (!group) {
            return null;
        }
        return group.identifier;
    }
    /**
     * Returns if the wizard is or not dynamic.
     *
     * @returns If the wizard is or not dynamic.
     */
    isDynamic() {
        return Boolean(this.state.dynamic);
    }
    /**
     * Sets if the wizard is or not dynamic.
     *
     * @param dynamic - If the wizard is or not dynamic
     */
    setDynamic(dynamic) {
        this.setState({ dynamic });
    }
    /**
     * Gets the dynamic wizard groups.
     *
     * @returns The dynamic wizard groups.
     */
    getDynamicGroups() {
        return this.state.dynamicGroups ?? [];
    }
    /**
     * Sets the dynamic wizard groups.
     *
     * @param dynamicGroups - The dynamic wizard groups
     */
    setDynamicGroups(dynamicGroups) {
        this.setState({ dynamicGroups });
    }
    /**
     * Gets the dynamic wizard steps.
     *
     * @returns The dynamic wizard steps.
     */
    getDynamicSteps() {
        return this.state.dynamicSteps ?? [];
    }
    /**
     * Sets the dynamic wizard steps.
     *
     * @param dynamicSteps - The dynamic wizard steps
     */
    setDynamicSteps(dynamicSteps) {
        this.setState({ dynamicSteps });
    }
    /**
     * Returns if the informed group is done.
     *
     * @param identifier - The group identifier
     *
     * @returns If the informed group is done.
     */
    isGroupDone(identifier) {
        const { contextGroups, steps } = this.state;
        const group = contextGroups.get(identifier);
        if (!group) {
            return false;
        }
        const groupSteps = Object.values(steps).filter(step => group.children.some(groupStep => groupStep.props.identifier === step.identifier));
        const updatedGroupSteps = groupSteps.map(step => this.getStep(step.identifier));
        return updatedGroupSteps.every(group => group?.done);
    }
    /**
     * Sets if the wizard group is done.
     *
     * @param identifier - The group identifier
     * @param done - If the group is done
     */
    setGroupDone(identifier, done) {
        const { contextGroups, current, steps } = this.state;
        const group = contextGroups.get(identifier);
        if (!group) {
            return;
        }
        const updatedGroupSteps = {};
        const groupSteps = Object.values(steps).filter(step => group.children.some(groupStep => groupStep.props.identifier === step.identifier));
        for (const groupStep of groupSteps) {
            updatedGroupSteps[groupStep.identifier] = { ...groupStep, done };
        }
        const lastGroupStepIndex = group.children.length - 1;
        const lastGroupStepIdentifier = group.children[lastGroupStepIndex].props.identifier;
        const abort = createAbort();
        this.onNavigate.fire(new AbortableNavigateEvent({ abort, from: current, to: lastGroupStepIdentifier }));
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            const stepsBeforeUpdate = {
                ...steps,
                ...updatedGroupSteps,
            };
            const result = editStep(current, lastGroupStepIdentifier, stepsBeforeUpdate);
            this.setState({
                current: result.identifier,
                previous: current,
                steps: updateStages(result.identifier, result.steps),
            });
            this.onAfterNavigate.fire(new NavigateEvent({ from: current, to: result.identifier }));
        });
    }
    /**
     * Gets the wizard mode.
     *
     * @returns The wizard mode.
     */
    getMode() {
        return this.state.mode;
    }
    /**
     * Sets the wizard mode.
     *
     * @param mode - The wizard mode
     */
    setMode(mode) {
        this.setState({ mode });
    }
    /**
     * Gets the next step button expression.
     *
     * @returns The next step button expression.
     */
    getNextStepExpression() {
        return this.state.nextStepExpression;
    }
    /**
     * Sets the next step button expression.
     *
     * @param nextStepExpression - The next step button expression.
     */
    setNextStepExpression(nextStepExpression) {
        this.setState({ nextStepExpression });
    }
    /**
     * Gets the previous step button expression.
     *
     * @returns The previous step button expression.
     */
    getPrevStepExpression() {
        return this.state.prevStepExpression;
    }
    /**
     * Sets the previous step button expression.
     *
     * @param prevStepExpression - The previous step button expression.
     */
    setPrevStepExpression(prevStepExpression) {
        this.setState({ prevStepExpression });
    }
    /**
     * Returns if the informed step is done.
     *
     * @param identifier - The step identifier
     *
     * @returns If the informed step is done.
     */
    isStepDone(identifier) {
        const step = this.getStep(identifier);
        return step ? step.done : false;
    }
    /**
     * Sets if the wizard step is done.
     *
     * @param identifier - The step identifier
     * @param done - If the step is done
     */
    setStepDone(identifier, done) {
        const { steps } = this.state;
        this.setState({
            steps: { ...steps, [identifier]: { ...steps[identifier], done } },
        });
    }
    /**
     * Fires submit event.
     */
    submit() {
        this.onSubmit.fire();
    }
    /**
     * Fires cancel event.
     */
    cancel() {
        this.onCancel.fire();
    }
    /**
     * Gets the wizard steps.
     *
     * @returns The wizard steps.
     */
    getSteps() {
        return this.state.steps;
    }
    /**
     * Sets the wizard hidden groups.
     *
     * @param hiddenGroups - The wizard hidden groups
     */
    setHiddenGroups(hiddenGroups) {
        this.setState({ hiddenGroups });
    }
    /**
     * Gets the wizard hidden groups.
     *
     * @returns The wizard hidden groups.
     */
    getHiddenGroups() {
        return this.state.hiddenGroups;
    }
    /**
     * Sets the wizard hidden steps.
     *
     * @param hiddenSteps - The wizard hidden steps
     */
    setHiddenSteps(hiddenSteps) {
        this.setState({ hiddenSteps });
    }
    /**
     * Gets the wizard hidden steps.
     *
     * @returns The wizard hidden steps.
     */
    getHiddenSteps() {
        return this.state.hiddenSteps;
    }
    /**
     * Gets if the wizard is read only.
     *
     * @returns If the wizard is read only.
     */
    isReadOnly() {
        return this.state.readOnly;
    }
    /**
     * Sets if the wizard is read only.
     *
     * @param readOnly - If the wizard is read only.
     */
    setReadOnly(readOnly) {
        return this.setState({ readOnly });
    }
    /**
     * Obtains if the wizard navigation dots should be visible.
     *
     * @returns If the wizard navigation dots should be visible.
     */
    isShowNavigationDots() {
        return this.state.showNavigationDots;
    }
    /**
     * Updates if the wizard navigation dots should be visible.
     *
     * @param showNavigationDots - If the wizard navigation dots should be visible.
     */
    setShowNavigationDots(showNavigationDots) {
        return this.setState({ showNavigationDots });
    }
    /**
     * Navigates to the informed step edition.
     *
     * @param identifier - The step identifier
     */
    editStep(identifier) {
        const currentIdentifier = this.getCurrentStepIdentifier();
        if (identifier === currentIdentifier) {
            return;
        }
        const identifiers = getIdentifiers(this.getSteps());
        const currentIndex = identifiers.findIndex((id) => id === currentIdentifier);
        const expectedIndex = identifiers.findIndex((id) => id === identifier);
        if (expectedIndex < 0) {
            throw new Error(`[Wizard API]: Could not find step with identifier "${identifier}".`);
        }
        if (expectedIndex > currentIndex) {
            throw new Error(`[Wizard API]: Can't go to a following step.`);
        }
        const abort = createAbort();
        this.onNavigate.fire(new AbortableNavigateEvent({ abort, from: currentIdentifier, to: identifier }));
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            const result = editStep(currentIdentifier, identifier, this.getSteps());
            this.setState({
                current: result.identifier,
                previous: currentIdentifier,
                steps: updateStages(result.identifier, result.steps),
            });
            this.onAfterNavigate.fire(new NavigateEvent({ from: currentIdentifier, to: result.identifier }));
        });
    }
    /**
     * Gets the informed step result.
     *
     * @param identifier - The step identifier
     */
    getStepResult(identifier) {
        const defaultRenderFn = () => null;
        let renderFn = defaultRenderFn;
        const render = (fn) => {
            renderFn = fn;
        };
        new RenderEvent({
            data: {},
            entry: identifier,
            render,
            type: 'step-result',
        });
        return renderFn();
    }
    /**
     * Navigates to the next step.
     */
    nextStep() {
        const { current, steps } = this.state;
        const { done } = steps[current];
        if (!done) {
            throw Error('[Wizard]: Current step is not done.');
        }
        const abort = createAbort();
        const nextIdentifier = findAdjacentStepIdentifier(AdjacentStep.NEXT, current, steps);
        this.onNavigate.fire(new AbortableNavigateEvent({ abort, from: current, to: nextIdentifier }));
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            const submitResult = submitStep(current, steps, (identifier) => this.getStepResult(identifier));
            this.setState({
                current: submitResult.identifier,
                previous: current,
                steps: updateStages(submitResult.identifier, submitResult.steps),
            });
            this.onAfterNavigate.fire(new NavigateEvent({ from: current, to: submitResult.identifier }));
        });
    }
    /**
     * Navigates to the previous step.
     */
    prevStep() {
        const { current, steps } = this.state;
        const abort = createAbort();
        const prevIdentifier = findAdjacentStepIdentifier(AdjacentStep.PREVIOUS, current, steps);
        this.onNavigate.fire(new AbortableNavigateEvent({ abort, from: current, to: prevIdentifier }));
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            const submitResult = editStep(current, prevIdentifier, steps, (identifier) => this.getStepResult(identifier));
            this.setState({
                current: submitResult.identifier,
                previous: current,
                steps: updateStages(submitResult.identifier, submitResult.steps),
            });
            this.onAfterNavigate.fire(new NavigateEvent({ from: current, to: submitResult.identifier }));
        });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onAfterNavigate", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onBeforeSubmit", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onBeforeCancel", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onCancel", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onDynamicContentRender", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onNavigate", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], WizardAPI.prototype, "onSubmit", void 0);
export default WizardAPI;
//# sourceMappingURL=wizard-api.js.map